import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import L from 'leaflet';
import { RouteService } from './route.service';
import { City } from '../map/interfaces';
import { MatDialog } from '@angular/material/dialog';
import { ErrorDialogComponent } from '../dialogs/error-dialog/error-dialog.component';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class MapService {

  constructor(
    private http : HttpClient,
    private httpService : ApiService,
    private routeService : RouteService,
    public dialogService: MatDialog
  ) { }

  private map : L.Map;

  private getView(){
    return document.getElementById("converter");
  }

  /*
  *   initializes the Leaflet Map with its layers
  *   returns the created map and saves the instance in this service
  */
  async initMap() : Promise<L.Map>{
    const map = L.map('map', {
      center : [ 49.93393, 7.327955 ],
      zoom : 9
    })

    this.map = map;

    this.initBaseLayer();
    this.initGrayRlpLayer();
    this.initBorderLayer();
    await this.initCitiesLayer();

    return map;
  }

  /*
  *   adds the base layer from cartocdn
  *   needs to be called after this.map is initialized
  */
  private initBaseLayer() {
    const tiles = L.tileLayer(
      "https://{s}.basemaps.cartocdn.com/rastertiles/voyager_no_labels_no_buildings/{z}/{x}/{y}{r}.png",
      {
          attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>, <a href="https://www.basemaps.cartocdn.com">Carto</a>',
          maxZoom: 13,
          minZoom: 9,
      }
    );

    tiles.addTo(this.map);
  }

  /*
  *   adds a gray layer above everything except rheinland pfalz to highlight it
  *   needs to be called after this.map is initialized
  */
  private initGrayRlpLayer() {
    this.http.get(environment.wordpressThemeUrl + 'assets/geo/world_without_rlp.geojson').subscribe(
      (geojson: any) => {
        const options: L.GeoJSONOptions = {
          style: {
            fillColor: "light_gray",
            fillOpacity: 0.6,
            stroke: false
          }
        };
        const rlp_polygon = L.geoJSON(geojson, options).addTo(this.map);
      }
    );
  }

  /*
  *   adds borders for districts in rlp
  *   needs to be called after this.map is initialized
  */
  private initBorderLayer() {
    this.http.get(environment.wordpressThemeUrl + 'assets/geo/borders_small_cities.geojson').subscribe(
      (geojson: any) => {
        const options: L.GeoJSONOptions = {
          style: {
            color: "gray",
            fillColor: "light_gray",
            fillOpacity: 0,
            opacity: 1
          }
        };
        const border_polygons = L.geoJSON(geojson, options).addTo(this.map);
      }
    );
  }

  /*
  *   fetch the cities, then display them using city svg icons
  *   in the content from the onclick: display the converter-view-component from the submodule
  *   needs to be called after this.map is initialized
  */
  private async initCitiesLayer() {
    let cities : City[];
    try{
      cities = await this.fetchCities();
    } catch(error : any){
      const dialogRef = this.dialogService.open(ErrorDialogComponent, {
        width: '900px'
      });
      dialogRef.componentRef.instance.errorDescription = 
        "The cities can not be found due to a connection issue. Maybe there is no connection to the database?";
      return;
    }
    const cityMarkers : L.Marker[] = [];

    const view = this.getView();
    if (!view) {
      throw new Error("Converter View can not be found in the html!");
    }
    cities.forEach((city) => {
      const marker = L.marker([city.position.lat, city.position.lon]);
      cityMarkers.push(marker);
      marker.on("click", () => {
        this.routeService.updateRouteID(city.id);
      });
      marker.setIcon(
        new L.DivIcon({
          className: 'city-icon-div',
          html: '<img class="city-icon" src="' + environment.wordpressThemeUrl +  'assets/img/location_city_FILL0_wght400_GRAD0_opsz24.svg"/>'+
                '<span class="city-icon-span">'+city.name+'</span>'
        })
      );
      const tooltip = document.createElement("h4");
      tooltip.textContent = city.name;
      marker.setTooltipContent(tooltip);
      marker.bindPopup(L.popup({
        minWidth: 600
      }).setContent(view));
    });

    const group = L.featureGroup(cityMarkers);
    group.addTo(this.map);
  }

  /*
  *   use a http request to get information on the cities from fud_demko_doctyp3
  *   returns all found cities with their document id, the name and the position
  *   if the http request is not successful, the error will be transmitted in the promise
  *   needs to be called after this.map is initialized
  */
  private async fetchCities() : Promise<City[]>{
    const cities : City[] = [];
    // use size ten thousand to receive (hopefully) all results, standard are 10
    const body = {
      "query": {
        "match": {
          "_index" : "fud_demko_doctyp3"
        }
      },
      "size" : 10000
    }
    return new Promise<City[]>((resolve : any, reject : any)=>{
      this.httpService.Search(body).subscribe((v:any)=>{
        
        const hits = v.hits.hits;
        hits.forEach((hit : any) => {
          const doc = hit._source;
          const stadt = doc["3_stadt"];
          if(stadt){
            const location = stadt.geo_point;
            const name = stadt.content;
            if(location){
              if(location.length === 2){
                cities.push({
                  id : hit._id,
                  name : name,
                  position : { 
                    lon : location[0],
                    lat : location[1]
                  }
                });
              }
            }
          }
        });
        resolve(cities);
      },
      (error : any) => {
        console.error("Something went wrong during the request on the cities from fud_demko_doctyp3");
        reject(error);
      });
    });
  }

}
